'use client';

// @boty/ui - BotyIcon component
// Boty mascot SVG icon with simple and full variants

import type { ReactNode } from 'react';

export type BotyIconVariant = 'simple' | 'full';

export interface BotyIconProps {
  /** Icon size in pixels (default: 32) */
  size?: number;
  /** 'simple' = chat bubble only, 'full' = includes neural network + AI badge (default: 'full') */
  variant?: BotyIconVariant;
  /** Additional CSS class name */
  className?: string;
}

/**
 * BotyIcon - Boty mascot SVG component
 *
 * A friendly chat bubble character with eyes, smile, and optional neural network decoration.
 *
 * **Color Note:** Uses hardcoded brand colors for consistency across all contexts:
 * - Indigo #4f46e5 (chat bubble body) - matches theme primary
 * - Light indigo #a5b4fc, #c7d2fe (neural network)
 * - Deep indigo #3730a3 (eye pupils)
 * - Orange #f97316 (AI badge) - matches theme secondary/accent
 *
 * Colors are hardcoded because SVG elements cannot use MUI sx theming.
 * If brand colors change, update both this component and the MUI theme.
 *
 * @param size - Icon size in pixels (default: 32)
 * @param variant - 'simple' (chat bubble only) or 'full' (with neural network + AI badge, default)
 * @param className - Additional CSS class name
 *
 * @example
 * ```tsx
 * // Default full variant at 32px
 * <BotyIcon />
 *
 * // Large icon
 * <BotyIcon size={64} />
 *
 * // Simple variant for favicons and small sizes
 * <BotyIcon variant="simple" size={16} />
 * ```
 */
export function BotyIcon({
  size = 32,
  variant = 'full',
  className,
}: BotyIconProps): ReactNode {
  return (
    <svg
      viewBox="0 0 128 128"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Boty mascot"
      className={className}
    >
      {/* Chat bubble body - always shown */}
      <path
        d="M64 4 C104 4 124 26 124 58 C124 90 104 110 64 110 C50 110 24 110 14 120 C14 106 14 96 14 86 C2 76 2 68 2 58 C2 26 24 4 64 4Z"
        fill="#4f46e5"
      />

      {/* Neural network lines - full variant only */}
      {variant === 'full' && (
        <>
          <line x1="20" y1="38" x2="40" y2="22" stroke="#a5b4fc" strokeWidth="2" opacity="0.8" />
          <line x1="40" y1="22" x2="64" y2="16" stroke="#a5b4fc" strokeWidth="2" opacity="0.8" />
          <line x1="64" y1="16" x2="88" y2="22" stroke="#a5b4fc" strokeWidth="2" opacity="0.8" />
          <line x1="88" y1="22" x2="108" y2="38" stroke="#a5b4fc" strokeWidth="2" opacity="0.8" />
          <line x1="40" y1="22" x2="64" y2="34" stroke="#a5b4fc" strokeWidth="2" opacity="0.6" />
          <line x1="64" y1="34" x2="88" y2="22" stroke="#a5b4fc" strokeWidth="2" opacity="0.6" />
          <line x1="64" y1="16" x2="64" y2="34" stroke="#a5b4fc" strokeWidth="2" opacity="0.6" />
        </>
      )}

      {/* Neural network nodes - full variant only */}
      {variant === 'full' && (
        <>
          <circle cx="20" cy="38" r="5" fill="#c7d2fe" />
          <circle cx="40" cy="22" r="6" fill="#a5b4fc" />
          <circle cx="64" cy="16" r="5" fill="#c7d2fe" />
          <circle cx="64" cy="34" r="7" fill="#a5b4fc" />
          <circle cx="88" cy="22" r="6" fill="#a5b4fc" />
          <circle cx="108" cy="38" r="5" fill="#c7d2fe" />
        </>
      )}

      {/* Eyes - always shown */}
      <ellipse cx="44" cy="58" rx="10" ry="12" fill="white" />
      <circle cx="46" cy="60" r="5" fill="#3730a3" />
      <ellipse cx="84" cy="58" rx="10" ry="12" fill="white" />
      <circle cx="86" cy="60" r="5" fill="#3730a3" />

      {/* Smile - always shown */}
      <path
        d="M44 82 Q64 98 84 82"
        stroke="white"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />

      {/* AI badge - full variant only */}
      {variant === 'full' && (
        <>
          <circle cx="108" cy="94" r="16" fill="#f97316" />
          <text
            x="108"
            y="100"
            textAnchor="middle"
            fill="white"
            fontSize="12"
            fontWeight="bold"
            fontFamily="sans-serif"
          >
            AI
          </text>
        </>
      )}
    </svg>
  );
}

BotyIcon.displayName = 'BotyIcon';
